import {Button, Checkbox, Col, Collapse, Divider, Radio, Row, Space} from "antd";
import React, {useEffect, useMemo, useState} from "react";
import {isEqual} from 'underscore';
import {formatTitleCase} from "@lib/format";
import {defaultIssueFilterProps, fixFilterObjectEmptyArrays} from "@components/issues-filter-drawer/IssueFilterDrawer";
import {IssueFilterDrawer} from "@components/issues-filter-drawer";
import {FilterIcon} from "@components/icons/Icons";
import useSubscriptionType from "@hooks/useSubscriptionType";
import {SCREEN, useWindowSize} from "@components/window-size-provider";
import {useGetFilterOptionsQuery} from "@mwServices/Issue";
import {transformEra} from "@lib/utils";

const { Panel } = Collapse;

const IssuesListFiltersContent = ({filters, setFilters, defaultFilters}) => {
    const subscriptionType = useSubscriptionType();
    const showPriceInfo = useMemo(() => ( subscriptionType.isTrial || subscriptionType.isUnlimited), [subscriptionType]);

    const queryParams = useMemo(() => ({filters: {...defaultFilters}}), [defaultFilters]);
    const filterOptionsQuery = useGetFilterOptionsQuery(queryParams);

    const {publishers, eras} = useMemo(() => {
        if(filterOptionsQuery.data) return filterOptionsQuery.data;
        return {publishers: [], eras: []};
    }, [filterOptionsQuery]);

    return (
        <>
            <Row>
                <Col span={24}>
                    <div className="font-18 font-bold">Sort by:</div>
                    <br/>

                    <Radio.Group
                        className="pad-left-10"
                        onChange={(event) => setFilters({...filters, sort: event.target.value})}
                        value={filters.sort}
                    >
                        <Space direction="vertical">
                            <Radio value={0} >Title A-Z</Radio>
                            <Radio value={showPriceInfo ? 1 : null} disabled={!showPriceInfo}>Est Value (Highest to Lowest)</Radio>
                            <Radio value={showPriceInfo ? 2 : null} disabled={!showPriceInfo}>Current Market Trend (%) (Highest to Lowest)</Radio>
                            <Radio value={6} >Comic Publish Date (Newest to Oldest)</Radio>
                        </Space>
                    </Radio.Group>

                    <br/>
                    {!showPriceInfo && (<p><br/><a className="upgrade-link" href="/plans/unlimited/">*Upgrade to use all filter options.</a></p>)}
                </Col>
            </Row>
            <Divider />
            <Row>
                <Col span={24}>
                    <div className="font-18 font-bold">Filter by:</div>

                    <Collapse className="filtersAccordion" ghost defaultActiveKey={[1,2,3,4]} expandIconPosition={"end"}>

                        {subscriptionType.isLoggedIn && (
                            <Panel header="In Collection" key={1}>
                                <Space direction="vertical">
                                    <Checkbox
                                        checked={!!filters?.inCollection}
                                        onChange={(event) => setFilters({
                                            ...filters, inCollection:
                                            event.target.checked
                                        })}
                                    >{formatTitleCase('Items In Your Collection')}
                                    </Checkbox>
                                </Space>
                            </Panel>
                        )}

                        {!defaultFilters.isTopVariant && (
                            <Panel header="Regular / Variants" key={2}>
                                <Space direction="vertical">
                                    <Checkbox
                                        checked={!!filters?.onlyVariants}
                                        onChange={(e) =>
                                            setFilters({
                                                ...filters,
                                                onlyVariants: e.target.checked
                                            })}
                                    >{formatTitleCase('Only Show Variants')}</Checkbox>
                                </Space>
                            </Panel>
                        )}

                        <Panel header="Era" key={3}>
                            <Space direction="vertical">
                                <Checkbox
                                    checked={!filters.multipleDateRanges}
                                    onChange={(event) => {
                                        setFilters({...filters, multipleDateRanges: event.target.checked ? undefined : []});
                                    }}
                                >All</Checkbox>
                                {eras.map((era, i) => (
                                    <Checkbox
                                        key={i}
                                        checked={!filters.multipleDateRanges || filters.multipleDateRanges.findIndex(dr => !!dr.id && dr.id == era.id) !== -1}
                                        onChange={(event) => {
                                            if(!filters.multipleDateRanges && !event.target.checked){
                                                setFilters({...filters, multipleDateRanges: eras.map(transformEra).filter(item => item.id !== era.id)});
                                            } else if(filters.multipleDateRanges) {
                                                let filterDateRanges: any = filters.multipleDateRanges ? [...filters.multipleDateRanges] : [];
                                                if (event.target.checked) {
                                                    filterDateRanges = [...filterDateRanges, transformEra(era)];
                                                } else {
                                                    filterDateRanges = !!filters.multipleDateRanges ? filters.multipleDateRanges.filter(item => !!item.id && item.id !== era.id) : [];
                                                }
                                                if (filterDateRanges.length === eras.length) filterDateRanges = undefined;
                                                setFilters({...filters, multipleDateRanges: filterDateRanges});
                                            }
                                        }}
                                    >
                                        {formatTitleCase(era.name, 'Unknown Era')} ({era.count})
                                    </Checkbox>
                                ))}
                            </Space>
                        </Panel>

                        <Panel header="Publisher" key={4}>
                            <Space direction="vertical">
                                <Checkbox
                                    checked={!filters.publishers}
                                    onChange={(event) => {
                                        setFilters({...filters, publishers: event.target.checked ? undefined : []});
                                    }}
                                >All</Checkbox>
                                {publishers.map((publisher,i) => (
                                    <Checkbox
                                        key={i}
                                        checked={!filters.publishers || filters.publishers.indexOf(publisher.id) !== -1}
                                        onChange={(event) => {
                                            if(!filters.publishers && !event.target.checked) {
                                                setFilters({...filters, publishers: publishers.map(item => item.id).filter(item => item !== publisher.id)});
                                            }else if(filters.publishers) {
                                                let filtered: any = filters.publishers ? [...filters.publishers] : [];
                                                if (event.target.checked) {
                                                    filtered = [...filtered, publisher.id]
                                                } else {
                                                    filtered = !!filters.publishers ? filters.publishers.filter(item => item !== publisher.id) : [];
                                                }
                                                if (filtered.length === publishers.length) filtered = undefined;
                                                setFilters({...filters, publishers: filtered});
                                            }
                                        }}
                                    >
                                        {formatTitleCase(publisher.title, 'Unknown Publisher')} ({publisher.count})
                                    </Checkbox>
                                ))}
                            </Space>
                        </Panel>

                    </Collapse>

                </Col>
            </Row>
        </>
    );
};

const IssuesListFiltersDrawer = ({ filters, setFilters, defaultFilters}) => {
    const windowDimensions = useWindowSize();
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [newFilters, setNewFilters] = useState(filters);

    useEffect(() => {
        if(!isEqual(newFilters, filters)) {
            setNewFilters(filters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const onClose = () => {
        setNewFilters(filters);
        setShowFilters(false);
    };

    return (
        <>
            <Button className="greyButton" size={"large"} onClick={() => setShowFilters(true)} >
                {windowDimensions.up(SCREEN.SM) && (<span className="filterButtonText">Filter / Sort</span>)}
                <FilterIcon className="font-12" />
            </Button>

            <IssueFilterDrawer visible={showFilters} onClose={onClose} onSearch={() => {
                setShowFilters(false);

                const fixedFilters = fixFilterObjectEmptyArrays(
                    newFilters,
                    filters,
                    ['publishers', 'multipleDateRanges']);

                if(fixedFilters.showValuesShown === -1) fixedFilters.showValuesShown = undefined;

                if(!isEqual(fixedFilters, filters)) {
                    setFilters(fixedFilters);
                }else if(!isEqual(fixedFilters, newFilters)) {
                    setNewFilters(fixedFilters);
                }
            }}>
                <IssuesListFiltersContent filters={newFilters} setFilters={setNewFilters} defaultFilters={defaultFilters} />
            </IssueFilterDrawer>
        </>
    )
};

IssuesListFiltersDrawer.defaultProps = defaultIssueFilterProps;

export default IssuesListFiltersDrawer;